import type { ColumnDef } from '@tanstack/react-table';
import { Check, X } from 'lucide-react';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionButton } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import {
  BOOKLIST_QUESTIONS,
  type BooklistAnswerRow,
  type SpecimenRequisitionRow,
  type SpPromoRequisitionMember,
  type SpPromoRequisitionRow,
  type StudentProject,
} from '../types/productRequisition.types';
import { Can } from '@/components/common/Can';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const qty = (value: number | null) =>
  value === null ? (
    <span className="text-muted-foreground">{EMPTY_VALUE}</span>
  ) : (
    <span className="tabular-nums">{formatNumber(value)}</span>
  );

const yesNo = (value: boolean) =>
  value ? (
    <Check className="size-4 text-success-foreground" aria-label="Yes" />
  ) : (
    <X className="size-4 text-muted-foreground" aria-label="No" />
  );

const stageCell = (row: {
  isReview: boolean;
  isApprovedLevelOne: boolean;
  isApprovedLevelTwo: boolean;
}): string => {
  if (row.isApprovedLevelTwo) return 'Approved (2nd)';
  if (row.isApprovedLevelOne) return 'Approved (1st)';
  if (row.isReview) return 'Reviewed';
  return 'Pending';
};

/* ---------------------- specimen requisition review ----------------------- */

export function getRequisitionReviewColumns(
  searchText: string | undefined,
  onDecide: (row: SpecimenRequisitionRow) => void,
): ColumnDef<SpecimenRequisitionRow, unknown>[] {
  return [
    createSerialColumn<SpecimenRequisitionRow>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={
            <HighlightText text={dash(row.original.instituteName)} highlightedText={searchText} />
          }
          secondary={dash(row.original.instituteTypeName)}
        />
      ),
    },
    {
      accessorKey: 'thanaName',
      header: 'Thana',
      cell: ({ row }) => dash(row.original.thanaName),
    },
    {
      accessorKey: 'specimenName',
      header: 'Specimen',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.specimenName} highlightedText={searchText} />}
          secondary={dash(row.original.specimenCode)}
        />
      ),
    },
    {
      accessorKey: 'memberName',
      header: 'Raised By',
      cell: ({ row }) => dash(row.original.memberName),
    },
    {
      accessorKey: 'requiredQty',
      header: 'Requisition Qty',
      cell: ({ row }) => qty(row.original.requiredQty),
    },
    {
      accessorKey: 'teacherQty',
      header: 'Teacher Qty',
      cell: ({ row }) => qty(row.original.teacherQty),
    },
    {
      accessorKey: 'studentQty',
      header: 'Student Qty',
      cell: ({ row }) => qty(row.original.studentQty),
    },
    {
      accessorKey: 'targetSaleQty',
      header: 'Target Sale Qty',
      cell: ({ row }) => qty(row.original.targetSaleQty),
    },
    {
      accessorKey: 'targetDate',
      header: 'Target Date',
      cell: ({ row }) =>
        row.original.targetDate === null ? EMPTY_VALUE : formatDisplayDate(row.original.targetDate),
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: ({ row }) => stageCell(row.original),
    },
    createActionsColumn<SpecimenRequisitionRow>((row) => (
      <Can code={PRODUCT_PERMISSIONS.SPECIMEN_REQUISITION_REVIEW_REVIEW}>
        <RowActionButton
          label="Decide"
          onClick={() => onDecide(row)}
          ariaLabel={`Decide the requisition for ${dash(row.instituteName)}`}
        />
      </Can>
    )),
  ];
}

/* --------------------- sp promotional requisition ------------------------- */

export function getSpPromoRequisitionColumns(
  onDrill: (row: SpPromoRequisitionRow) => void,
): ColumnDef<SpPromoRequisitionRow, unknown>[] {
  return [
    createSerialColumn<SpPromoRequisitionRow>(),
    {
      accessorKey: 'specimenCode',
      header: 'BAR Code',
      cell: ({ row }) => dash(row.original.specimenCode),
    },
    {
      accessorKey: 'specimenId',
      header: 'Specimen Code',
      cell: ({ row }) => <span className="tabular-nums">{row.original.specimenId}</span>,
    },
    {
      accessorKey: 'specimenName',
      header: 'Specimen Name (Eng)',
      cell: ({ row }) => (
        <WrappingCellText width="wide">{row.original.specimenName}</WrappingCellText>
      ),
    },
    {
      accessorKey: 'specimenNameBn',
      header: 'Specimen Name (Bng)',
      cell: ({ row }) => (
        <WrappingCellText width="wide">{dash(row.original.specimenNameBn)}</WrappingCellText>
      ),
    },
    {
      accessorKey: 'requiredQty',
      header: 'Requisition Qty',
      cell: ({ row }) => (
        <RowActionButton
          label={formatNumber(row.original.requiredQty)}
          onClick={() => onDrill(row.original)}
          ariaLabel={`Who asked for ${row.original.specimenName}`}
        />
      ),
    },
    {
      accessorKey: 'teacherQty',
      header: 'Teacher Qty',
      cell: ({ row }) => qty(row.original.teacherQty),
    },
    {
      accessorKey: 'targetSaleQty',
      header: 'Target Sale Qty',
      cell: ({ row }) => qty(row.original.targetSaleQty),
    },
  ];
}

export function getSpPromoMemberColumns(): ColumnDef<SpPromoRequisitionMember, unknown>[] {
  return [
    createSerialColumn<SpPromoRequisitionMember>(),
    {
      accessorKey: 'memberName',
      header: 'Member',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.memberName)}
          secondary={dash(row.original.username)}
        />
      ),
    },
    {
      accessorKey: 'mobile',
      header: 'Mobile',
      cell: ({ row }) => dash(row.original.mobile),
    },
    {
      accessorKey: 'designationName',
      header: 'Designation',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.designationName)}
          secondary={dash(row.original.departmentName)}
        />
      ),
    },
    {
      id: 'area',
      header: 'Area',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.thanaName)}
          secondary={[row.original.districtName, row.original.divisionName]
            .filter((part) => part !== null)
            .join(', ')}
        />
      ),
    },
    {
      accessorKey: 'requiredQty',
      header: 'Requisition Qty',
      cell: ({ row }) => qty(row.original.requiredQty),
    },
    {
      accessorKey: 'approvedQty',
      header: 'Reviewed Qty',
      cell: ({ row }) => qty(row.original.approvedQty),
    },
    {
      accessorKey: 'teacherQty',
      header: 'Teacher Qty',
      cell: ({ row }) => qty(row.original.teacherQty),
    },
    {
      accessorKey: 'targetSaleQty',
      header: 'Target Sale Qty',
      cell: ({ row }) => qty(row.original.targetSaleQty),
    },
  ];
}

/* ---------------------- mkt student collection project -------------------- */

interface StudentProjectActions {
  // Absent for anyone legacy would not show the Edit link to.
  onEdit?: ((row: StudentProject) => void) | undefined;
  onEnter: (row: StudentProject) => void;
}

export function getStudentProjectColumns(
  searchText: string | undefined,
  actions: StudentProjectActions,
): ColumnDef<StudentProject, unknown>[] {
  return [
    createSerialColumn<StudentProject>(),
    {
      accessorKey: 'name',
      header: 'Project Name',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Project Code',
      cell: ({ row }) => dash(row.original.code),
    },
    {
      accessorKey: 'isActive',
      header: 'Is Active',
      cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive'),
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      cell: ({ row }) =>
        row.original.startDate === null ? EMPTY_VALUE : formatDisplayDate(row.original.startDate),
    },
    {
      accessorKey: 'deadlineDate',
      header: 'Deadline Date',
      cell: ({ row }) =>
        row.original.deadlineDate === null
          ? EMPTY_VALUE
          : formatDisplayDate(row.original.deadlineDate),
    },
    {
      accessorKey: 'instituteCount',
      header: 'Institutes',
      cell: ({ row }) => qty(row.original.instituteCount),
    },
    {
      accessorKey: 'studentQty',
      header: 'Students',
      cell: ({ row }) => qty(row.original.studentQty),
    },
    createActionsColumn<StudentProject>((row) => {
      const onEdit = actions.onEdit;

      return (
        <>
          <RowActionButton
            label="Student Entry"
            onClick={() => actions.onEnter(row)}
            ariaLabel={`Enter students for ${row.name}`}
          />
          {onEdit === undefined ? null : (
            <RowActionButton
              label="Edit"
              onClick={() => onEdit(row)}
              ariaLabel={`Edit ${row.name}`}
            />
          )}
        </>
      );
    }),
  ];
}

/* ------------------------ booklisted institution -------------------------- */

export function getBooklistAnswerColumns(
  onDecide: (row: BooklistAnswerRow) => void,
): ColumnDef<BooklistAnswerRow, unknown>[] {
  return [
    createSerialColumn<BooklistAnswerRow>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.instituteName}
          secondary={dash(row.original.instituteTypeName)}
        />
      ),
    },
    {
      accessorKey: 'thanaName',
      header: 'Thana',
      cell: ({ row }) => dash(row.original.thanaName),
    },
    {
      accessorKey: 'bookEditionName',
      header: 'Edition',
      cell: ({ row }) => row.original.bookEditionName,
    },
    {
      accessorKey: 'memberName',
      header: 'Answered By',
      cell: ({ row }) => dash(row.original.memberName),
    },
    ...BOOKLIST_QUESTIONS.map(
      ({ key, label }): ColumnDef<BooklistAnswerRow, unknown> => ({
        id: key,
        header: label,
        cell: ({ row }) => yesNo(row.original[key]),
      }),
    ),
    {
      id: 'stage',
      header: 'Stage',
      cell: ({ row }) => stageCell(row.original),
    },
    createActionsColumn<BooklistAnswerRow>((row) => (
      <Can code={PRODUCT_PERMISSIONS.BOOKLISTED_INSTITUTE_REVIEW}>
        <RowActionButton
          label="Decide"
          onClick={() => onDecide(row)}
          ariaLabel={`Decide the answer for ${row.instituteName}`}
        />
      </Can>
    )),
  ];
}
